import { useVirtualizer } from "@tanstack/react-virtual";
import { useRef } from "react";

type TVirtualizedList<T> = {
	items: T[];
	estimateSize: number;
	overscan?: number;
	renderItem: (item: T, index: number) => React.ReactNode;

	lastElem?: React.ReactNode;
} & React.ComponentPropsWithoutRef<"div">;

const Virtualizer = <T,>({ items, estimateSize, overscan = 5, renderItem, lastElem = <></>, style, ...props }: TVirtualizedList<T>) => {
	const parentRef = useRef<HTMLDivElement | null>(null);

	const rowVirtualizer = useVirtualizer({
		count: items.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => estimateSize,
		overscan,
	});

	return (
		<div
			ref={parentRef}
			{...props}
			style={{
				maxHeight: "100%",
				overflow: "auto",
				overflowX: "hidden",
				...style,
			}}
		>
			<div
				style={{
					height: rowVirtualizer.getTotalSize(),
					position: "relative",
					overflowX: "hidden",
				}}
			>
				{rowVirtualizer.getVirtualItems().map((virtualRow) => {
					const item = items[virtualRow.index];

					return (
						<div
							key={virtualRow.key}
							ref={rowVirtualizer.measureElement}
							data-index={virtualRow.index}
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								transform: `translateY(${virtualRow.start}px)`,
							}}
						>
							{renderItem(item, virtualRow.index)}
						</div>
					);
				})}
			</div>
			{lastElem}
		</div>
	);
};

export default Virtualizer;
